import React, { useState } from "react";
import {
  CornerUpLeft,
  Check,
  AlertTriangle,
  Clock,
  Send,
  Sparkles,
  Paperclip,
  ImageIcon,
  Search,
  IndianRupee,
  Languages,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { translateApi } from "../../lib/api";
import { looksNonEnglish } from "./utils";

/**
 * MessageBubble — single chat-bubble inside the messages thread.
 *
 * Handles every variant a WATI message can take:
 *   - Internal-only system notes (centered amber pill, never sent to WATI)
 *   - Incoming / outgoing text bubbles with URL linkification
 *   - Image attachments (with click-to-zoom, "Find product" + "Verify Payment" actions)
 *   - Video / audio / generic attachments
 *   - AI-reply / broadcast / read-receipt indicators
 *   - Inline Translate button (Claude AI) for non-English messages
 *   - Failed-send banner with Resend button
 *
 * Extracted from ChatsPage.jsx Apr 2026 as part of the refactor sprint.
 */
const MessageBubble = ({ message, formatTime, onImageClick, onImageSearch, onReply, onResend, onVerifyPayment }) => {
  const [imgError, setImgError] = useState(false);
  const [translation, setTranslation] = useState(null);     // { translated, source_language }
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(null);

  const handleTranslate = async () => {
    if (translation || translating) return;
    setTranslating(true); setTranslateError(null);
    try {
      const res = await translateApi.translate(message.content || "", "English");
      setTranslation(res.data);
    } catch (e) {
      setTranslateError(e.response?.data?.detail || "Translate failed");
    } finally {
      setTranslating(false);
    }
  };
  // Internal-only system notes — render as a centered amber pill, never sent to WATI.
  // Used for AI handoffs, ticket reopen/close audit, reassignment notes, etc.
  if (message.direction === "system" || message.internal_only) {
    return (
      <div className="flex justify-center my-2 animate-fade-up" data-testid={`system-note-${message.id}`}>
        <div className="inline-block max-w-[80%] bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1 text-[11px] font-medium shadow-sm">
          <span className="opacity-70 mr-1">[Internal]</span>
          {message.content}
          <span className="ml-2 text-[9px] text-amber-700 opacity-70">{formatTime(message.timestamp)}</span>
        </div>
      </div>
    );
  }
  const isOut = message.direction === "outgoing" || message.is_broadcast === true;
  const isBroadcast = message.is_broadcast === true;
  const mediaUrl = typeof message.media_url === "string" ? message.media_url : null;
  const hasMedia = !!mediaUrl;
  // HYBRID AUTH (Apr 30, 2026): <img src=...> tags can't send Bearer headers,
  // only cookies. In cross-origin deploys the cookie may be blocked, so we
  // append the JWT as a query param (`?token=...`) on same-origin CRM
  // media URLs. Backend `/api/wati/media` accepts both cookie AND query
  // param auth — see server.py `proxy_wati_media`.
  const _authToken = (() => { try { return localStorage.getItem("crm_token") || ""; } catch { return ""; } })();
  const _appendToken = (u) => {
    if (!u || !_authToken) return u;
    if (!u.startsWith("/api/")) return u;
    return u + (u.includes("?") ? "&" : "?") + "token=" + encodeURIComponent(_authToken);
  };
  const fullMediaUrl = mediaUrl && mediaUrl.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${_appendToken(mediaUrl)}`
    : mediaUrl;
  // Detect type safely — some WATI payloads nest `type` as an object.
  const asStr = (v) => {
    if (typeof v === "string") return v;
    if (v && typeof v === "object") return String(v.type || v.mime || v.mimeType || "");
    return v == null ? "" : String(v);
  };
  const msgType = [message.wati_type, message.messageType, message.type, message.mime_type]
    .map(asStr)
    .join(" ")
    .toLowerCase();

  const extMatches = (re) => !!(mediaUrl && re.test(mediaUrl));
  // Heuristic for older WATI rows that lack a type hint — see ChatsPage history
  // for the full rationale (lead 916281313567 was the one that surfaced this).
  const watiProxy = mediaUrl && mediaUrl.startsWith("/api/wati/media");
  const watiFileNameHint = watiProxy
    ? (() => {
        try {
          const q = mediaUrl.split("?")[1] || "";
          const params = new URLSearchParams(q);
          return (params.get("fileName") || "").toLowerCase();
        } catch {
          return "";
        }
      })()
    : "";
  const isImage = hasMedia && (
    msgType.includes("image") ||
    extMatches(/\.(jpe?g|png|webp|gif|bmp|heic)(\?|$)/i) ||
    (mediaUrl && (mediaUrl.includes("/images/") || mediaUrl.includes("image"))) ||
    watiFileNameHint.includes("/images/") ||
    watiFileNameHint.includes("image") ||
    (watiProxy && !msgType && !extMatches(/\.(mp4|mov|webm|mp3|wav|ogg|m4a|pdf|docx?|xlsx?|pptx?)(\?|$)/i))
  );
  const isVideo = hasMedia && !isImage && (
    msgType.includes("video") ||
    extMatches(/\.(mp4|mov|webm|avi|m4v|3gp|mkv)(\?|$)/i) ||
    (mediaUrl && mediaUrl.includes("/videos/"))
  );
  const isAudio = hasMedia && !isImage && !isVideo && (
    msgType.includes("audio") || msgType.includes("voice") || msgType.includes("ptt") ||
    extMatches(/\.(mp3|wav|ogg|m4a|aac|opus)(\?|$)/i)
  );

  // Message status tick indicator (single gray = sent, double gray = delivered, double blue = read)
  const getStatusIcon = () => {
    if (!isOut) return null;
    const status = (message.status || message.statusString || "").toUpperCase();
    if (status === "READ") {
      return <span className="inline-flex text-[--accent]" title="Read"><Check size={12} /><Check size={12} className="-ml-1.5" /></span>;
    } else if (status === "DELIVERED") {
      return <span className="inline-flex text-gray-400" title="Delivered"><Check size={12} /><Check size={12} className="-ml-1.5" /></span>;
    } else if (status === "SENT") {
      return <span className="text-gray-400" title="Sent"><Check size={12} /></span>;
    } else if (status === "FAILED") {
      return <span className="text-red-500" title="Failed"><AlertTriangle size={12} /></span>;
    } else if (status === "PENDING") {
      return <span className="text-gray-300" title="Pending"><Clock size={10} /></span>;
    }
    return <span className="text-gray-300" title="Sent"><Check size={12} /></span>;
  };

  // "> quote\n\nreply" (WhatsApp quote format, sent by the mobile app's swipe-to-reply)
  const splitQuote = (content) => {
    if (typeof content !== "string" || !content.startsWith("> ")) return { quote: "", body: content };
    const idx = content.indexOf("\n\n");
    if (idx < 0) return { quote: "", body: content };
    const quote = content.slice(0, idx).split("\n").map((l) => l.replace(/^>\s?/, "")).join(" ").trim();
    return { quote, body: content.slice(idx + 2) };
  };

  // Make URLs in content clickable
  const renderContent = (text) => {
    // Defence: WATI sometimes stores content as an object (e.g., for interactive
    // / list messages) which crashes String.prototype.split. Coerce to string.
    if (text == null) return null;
    if (typeof text !== "string") {
      try { text = typeof text === "object" ? JSON.stringify(text) : String(text); }
      catch { return null; }
    }
    if (!text || text === "[Media]") return null;
    const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => urlRegex.test(part)
      ? <a
          key={`url-${i}-${part.slice(0, 20)}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          // overflowWrap+wordBreak together force the URL to wrap inside any
          // bubble width, defeating the parent bubble's `overflow-hidden`
          // (which would otherwise clip a long single-token URL with `…`).
          // Yash Apr 26: live customers were seeing truncated URLs.
          style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
          className="text-blue-600 underline"
        >{part}</a>
      : part
    );
  };
  return (
    <div className={`flex mb-2 group ${isOut ? "justify-end" : "justify-start"}`} data-testid={`msg-${message.id}`}>
      {/* Reply button - appears on hover */}
      {isOut && onReply && (
        <button onClick={() => onReply(message)} className="opacity-0 group-hover:opacity-100 transition-opacity self-center mr-1 p-1 hover:bg-gray-100 rounded" title="Reply" data-testid={`reply-btn-${message.id}`}>
          <CornerUpLeft size={13} className="text-[--text-muted]" />
        </button>
      )}
      <div className={`max-w-[65%] rounded-2xl text-[13.5px] leading-relaxed overflow-hidden ${isOut ? "bg-[--bubble-out] text-[--text-primary] rounded-br-md" : "bg-white text-[--text-primary] rounded-bl-md"} ${(message.status || "").toUpperCase() === "FAILED" ? "ring-2 ring-red-300" : ""}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        {isImage && fullMediaUrl && !imgError && <div className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => onImageClick?.(fullMediaUrl)} data-testid={`msg-image-${message.id}`}><img src={fullMediaUrl} alt="" className="w-full max-h-[200px] object-cover rounded-t-xl" onError={(e) => { console.error("[CRM image load failed]", { src: e.target.src, message_id: message.id, raw_media_url: message.media_url }); setImgError(true); }} loading="lazy" /></div>}
        {isImage && imgError && fullMediaUrl && (
          <a href={fullMediaUrl} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center gap-1 p-4 bg-gray-50 border border-gray-200 text-gray-600 text-[11px] hover:bg-gray-100 rounded-t-xl break-all" data-testid={`msg-image-fallback-${message.id}`}>
            <Paperclip size={14} />
            <span>Image unavailable — click to open in WATI</span>
            <span className="text-[9px] text-gray-400 font-mono mt-1 px-2 text-center" title={message.media_url}>
              raw: {(message.media_url || "—").slice(0, 80)}
            </span>
          </a>
        )}
        {isVideo && fullMediaUrl && (
          <div className="relative rounded-t-xl overflow-hidden bg-black" data-testid={`msg-video-${message.id}`}>
            <video
              controls
              preload="metadata"
              playsInline
              className="w-full max-h-[260px] object-contain bg-black"
              onError={() => setImgError(true)}
            >
              <source src={fullMediaUrl} />
              Your browser can't play this video.
            </video>
          </div>
        )}
        {isAudio && fullMediaUrl && (
          <div className="p-2" data-testid={`msg-audio-${message.id}`}>
            <audio controls preload="metadata" className="w-full max-w-[260px]">
              <source src={fullMediaUrl} />
            </audio>
          </div>
        )}
        {hasMedia && !isImage && !isVideo && !isAudio && (
          <a href={fullMediaUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 p-2.5 bg-gray-50 border-b border-gray-200 text-gray-700 text-[12px] hover:bg-gray-100" data-testid={`msg-attachment-${message.id}`}>
            <Paperclip size={12} />
            <span className="truncate">{msgType || "Attachment"}</span>
          </a>
        )}
        {isImage && !isOut && fullMediaUrl && onImageSearch && (
          <button onClick={(e) => { e.stopPropagation(); onImageSearch(fullMediaUrl); }}
            className="mx-3 mt-1 text-[10px] text-purple-600 hover:text-purple-800 flex items-center gap-1 hover:underline" data-testid={`search-product-${message.id}`}>
            <Search size={11} /> Find this product on website
          </button>
        )}
        {isImage && !isOut && fullMediaUrl && onVerifyPayment && (
          <button onClick={(e) => { e.stopPropagation(); onVerifyPayment(message); }}
            className="mx-3 mt-1 mb-1 text-[10px] font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 hover:underline"
            data-testid={`verify-payment-${message.id}`}
            title="Ask AI whether this screenshot proves a real payment to Exotic India">
            <IndianRupee size={11} /> Verify Payment with AI
          </button>
        )}
        {hasMedia && !isImage && !isVideo && !isAudio && <div className="px-3 pt-2 flex items-center gap-1 text-[--text-muted]"><ImageIcon size={14} /><a href={fullMediaUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] underline">View attachment</a></div>}
        <div className="px-3 py-2">
          {message.is_broadcast && <p className="text-[9px] font-bold text-amber-600 mb-1 flex items-center gap-1" data-testid={`broadcast-label-${message.id}`}><Send size={9} /> Broadcast</p>}
          {message.is_ai_reply && <p className="text-[9px] font-bold text-purple-600 mb-1 flex items-center gap-1" data-testid={`ai-label-${message.id}`}><Sparkles size={9} /> AI Auto-Reply</p>}
          {message.content && message.content !== "[Media]" && (() => {
            const { quote, body } = splitQuote(message.content);
            return (
              <>
                {quote && (
                  <div data-testid={`msg-quote-${message.id}`} className="mb-1.5 rounded-md border-l-[3px] border-orange-500 bg-black/5 px-2 py-1 text-[13px] text-stone-600 line-clamp-3">
                    {quote}
                  </div>
                )}
                <p className="whitespace-pre-wrap break-words">{renderContent(body)}</p>
              </>
            );
          })()}
          {/* Translate button — shows for incoming messages that are likely
              non-English. Click translates via Claude into English inline. */}
          {!isOut && message.content && message.content !== "[Media]" && looksNonEnglish(message.content) && (
            <div className="mt-1.5" data-testid={`translate-row-${message.id}`}>
              {!translation && !translating && !translateError && (
                <button
                  onClick={handleTranslate}
                  className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 font-medium"
                  data-testid={`translate-btn-${message.id}`}
                  title="Translate to English"
                >
                  <Languages size={10} /> Translate to English
                </button>
              )}
              {translating && (
                <span className="text-[10px] text-blue-600 flex items-center gap-1" data-testid={`translate-loading-${message.id}`}>
                  <Loader2 size={10} className="animate-spin" /> Translating…
                </span>
              )}
              {translation && (
                <div className="bg-blue-50/70 border-l-2 border-blue-300 rounded-md px-2 py-1.5 mt-1" data-testid={`translation-${message.id}`}>
                  <p className="text-[9px] font-bold text-blue-700 uppercase tracking-wider mb-0.5">
                    {translation.source_language ? `${translation.source_language} → English` : "Translation"}
                  </p>
                  <p className="text-[12px] text-blue-900 whitespace-pre-wrap break-words">{translation.translated}</p>
                </div>
              )}
              {translateError && (
                <span className="text-[10px] text-red-500 flex items-center gap-1" data-testid={`translate-error-${message.id}`}>
                  <AlertTriangle size={10} /> {translateError}
                  <button onClick={() => { setTranslateError(null); }} className="underline ml-1">retry</button>
                </span>
              )}
            </div>
          )}
          {(message.status || "").toUpperCase() === "FAILED" && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg" data-testid={`failed-banner-${message.id}`}>
              <p className="text-[10px] font-bold text-red-700 flex items-center gap-1"><AlertTriangle size={10} /> Not delivered on WhatsApp</p>
              {message.send_error && (
                <p className="text-[10px] text-red-600 mt-0.5 break-words">{message.send_error}</p>
              )}
              <p className="text-[9px] text-red-500 mt-1 italic">
                Common causes: 24-hour session window expired, customer opted out, or WATI rate limit. Retry via agent message.
              </p>
              {onResend && (
                <button
                  onClick={() => onResend(message)}
                  className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold py-1 px-2 rounded-md flex items-center justify-center gap-1 transition-colors"
                  data-testid={`resend-btn-${message.id}`}
                >
                  <RefreshCw size={10} /> Resend via WhatsApp
                </button>
              )}
            </div>
          )}
          <p className="text-[9px] mt-1 text-right text-[--text-muted] flex items-center justify-end gap-1">
            {isBroadcast && <span className="bg-purple-100 text-purple-700 border border-purple-200 rounded-full px-1.5 py-0 text-[8px] font-bold uppercase tracking-wide" data-testid="broadcast-tag">Broadcast</span>}
            {formatTime(message.timestamp)}
            {getStatusIcon()}
          </p>
        </div>
      </div>
      {/* Reply button for incoming messages */}
      {!isOut && onReply && (
        <button onClick={() => onReply(message)} className="opacity-0 group-hover:opacity-100 transition-opacity self-center ml-1 p-1 hover:bg-gray-100 rounded" title="Reply" data-testid={`reply-btn-${message.id}`}>
          <CornerUpLeft size={13} className="text-[--text-muted]" />
        </button>
      )}
    </div>
  );
};

export default MessageBubble;
